/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, Input, ChangeDetectionStrategy, Output,
  EventEmitter, AfterViewInit, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { Observable, Subscription, combineLatest, of } from 'rxjs';

import {
  PickedRange, CheckboxFilterVm, CheckboxFilter, RangePicketVm, RangeType, PriceRangeFilter, PriceFilter
} from '@shared/filters/entities/fetch-filters.response';
import { HotelFilterApplier, HotelFilterInputTypes, HotelFilterTypes } from '@shared/hotel-itinerary/hotel-filters';
import { getHotelItemLabel } from '@shared/filters/mappers/hotel-filters-label.mapper';
import {
  HOTEL_NON_REFUNDABLE_TITLES, NO_ZONE_LABEL, STAR_LABEL, STARS_LABEL
} from '@hotels/hotel-results/constants/filters';
import { isTagTypeisPrime, isTagTypeisRappicard } from '@app/mappers/filters.mapper';
import { SharedSelectedFilterHotelValue } from '@shared/magic-filter-bar/entities/shared-props';
import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import {
  pickedCheckboxes as pickedCheckboxesMapper, pickedRanges as pickedRangesMapper, pickedStars as pickedStarsMapper
} from '@hotels/hotel-results/mappers/hotel-filters.mapper';
import { HotelFilterBarService } from '@shared/magic-filter-bar/hotel-filter-bar/services/hotel-filter-bar.service';
import {
  evalHasFilterByExcludeCollection, HOTEL_FILTER_BAR_TAG_ICONS, HOTEL_FILTER_BAR_TAGS, PRIME_KEY, RAPPICARD_KEY
} from '@shared/magic-filter-bar/hotel-filter-bar/mappers/hotel-filters-in-bar.mapper';
import { HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { hotelFilterIsNoZoneLabel, hotelFilterNeedsTranslation } from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { getLegendByScore } from '@shared/score-detail/helpers/label-score.helper';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-selected-hotel-filters',
  templateUrl: './selected-hotel-filters.container.html',
  styleUrls: ['./selected-hotel-filters.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SelectedHotelFiltersContainer implements AfterViewInit, OnDestroy {

  @Input() filters: HotelFilterVm[] = [];
  @Input() isMobile = true;
  @Input() hasRappiCard: boolean;
  @Input() isPrime: boolean;
  @Input() source: string;
  @Input() inFilterBar: boolean;

  @Output() remove: EventEmitter<HotelFilterApplier> = new EventEmitter<HotelFilterApplier>();

  @Output() sharedShouldShowFilters = new EventEmitter<boolean>();
  @Output() sharedSelectedHotelFilters = new EventEmitter<{ [key: string]: SharedSelectedFilterHotelValue }>();

  public exclusiveOffer: boolean;
  public pickedCheckboxes: string[] = [];
  public pickedStars: string[] = [];
  public pickedRanges = {} as PickedRange;
  public pickedTotalDiscountPercentage: number;
  public hotelNameSearch: string;

  protected selectedStarItems: CheckboxFilter[] = [];

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly translateService: TranslocoService,
    private readonly hotelResultsFacade: HotelResultsFacade,
    private readonly hotelFilterBarService: HotelFilterBarService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.exclusiveOfferFilter$.pipe(distinctUntilChanged()),
        this.pickedTotalDiscountPercentage$.pipe(distinctUntilChanged()),
        this.pickedCheckboxes$.pipe(distinctUntilChanged()),
        this.pickedStars$.pipe(distinctUntilChanged()),
        this.pickedRanges$.pipe(distinctUntilChanged()),
        this.hotelNameSearch$.pipe(distinctUntilChanged()),
        this.shouldShowFilters$.pipe(distinctUntilChanged()),
      ]).pipe(
        tap(([
          exclusiveOfferFilter, totalDiscountPercentage, checkboxes, starts, ranges, searchName, shouldShowFilters
        ]: [boolean, number, string[], string[], PickedRange, string, boolean]) => {
          this.exclusiveOffer = exclusiveOfferFilter;
          this.pickedCheckboxes = checkboxes;
          this.pickedStars = starts;
          this.pickedRanges = ranges;
          this.pickedTotalDiscountPercentage = totalDiscountPercentage;
          this.hotelNameSearch = searchName;
          this.cdr.detectChanges();
          if (this.inFilterBar) {
            this.sharedShouldShowFilters.emit(shouldShowFilters);
            this.sharedSelectedHotelFilters.emit({
              hotelNameSearch: searchName,
              selectedStarItems: this.selectedStarItems,
              pickedCheckboxes: checkboxes,
              pickedRanges: ranges,
              pickedMemberRatesOnly: exclusiveOfferFilter,
              pickedStars: starts,
              pickedTotalDiscountPercentage: totalDiscountPercentage,
            });
          }
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get viewSelectedFilters$(): Observable<boolean> {
    return this.inFilterBar ? this.shouldShowFilters$ : of(true);
  }

  get hasHotelNameSearchInputFilter(): boolean {
    return !!this.hotelNameSearch;
  }

  get hasExclusiveOffer(): boolean {
    return evalHasFilterByExcludeCollection(
      HotelFilterTypes.MemberRatesOnly, this.inFilterBar, true, this.exclusiveOffer, null
    );
  }

  get hasTotalDiscountPercentageFilter(): boolean {
    return evalHasFilterByExcludeCollection(
      HotelFilterTypes.PromotionalRates, this.inFilterBar, false,
      !!this.pickedTotalDiscountPercentage, this.totalDiscountPercentageText
    );
  }

  get totalDiscountPercentageText(): string {
    const promotionalRatesFilter: PriceRangeFilter[] =
      this.filters.find(filter => filter.type === HotelFilterTypes.PromotionalRates)?.value as PriceRangeFilter[];
    return promotionalRatesFilter &&
      promotionalRatesFilter.find(value => value.LowerBound === this.pickedTotalDiscountPercentage)?.Text;
  }

  get tagTypeisPrime(): boolean {
    return isTagTypeisPrime(this.source, this.isPrime);
  }

  get tagTypeisRappicard(): boolean {
    return isTagTypeisRappicard(this.source, this.hasRappiCard);
  }

  private get exclusiveOfferFilter$(): Observable<boolean> {
    return this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => payload.MemberRatesOnly));
  }

  private get hotelNameSearch$(): Observable<string> {
    return this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => payload.HotelName));
  }

  private get pickedTotalDiscountPercentage$(): Observable<number> {
    return this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => payload.TotalDiscountPercentageFrom));
  }

  private get pickedCheckboxes$(): Observable<string[]> {
    return this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => pickedCheckboxesMapper(payload)));
  }

  private get pickedStars$(): Observable<string[]> {
    return this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => pickedStarsMapper(payload)));
  }

  private get pickedRanges$(): Observable<PickedRange> {
    return this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => pickedRangesMapper(payload)));
  }

  private get shouldShowFilters$(): Observable<boolean> {
    return combineLatest([
      this.pickedCheckboxes$, this.pickedStars$, this.pickedRanges$, this.exclusiveOfferFilter$,
      this.pickedTotalDiscountPercentage$,
    ]).pipe(
      map(([checkboxes, stars, ranges, exclusiveOff, totalDiscountPercentage]) =>
        !isEmpty(checkboxes) || !isEmpty(stars) || !isEmpty(ranges) || this.hasHotelNameSearchInputFilter ||
        exclusiveOff || totalDiscountPercentage > 0));
  }

  public exclusiveLabel(filter: HotelFilterVm): string {
    const key = this.tagTypeisPrime ? PRIME_KEY : true ? RAPPICARD_KEY : '';
    const tranlateKey = HOTEL_FILTER_BAR_TAGS[filter.type]?.[key];
    return !isEmpty(tranlateKey) ? this.translateService.translate(tranlateKey) : '';
  }

  public exclusiveIconTag(filter: HotelFilterVm): Icon {
    const key = this.tagTypeisPrime ? PRIME_KEY : true ? RAPPICARD_KEY : '';
    return HOTEL_FILTER_BAR_TAG_ICONS[filter.type]?.[key];
  }

  public withTranslations(type: HotelFilterTypes): boolean {
    return hotelFilterNeedsTranslation(type);
  }

  public getItemLabel(label: string): string {
    return getHotelItemLabel(label);
  }

  public checkboxFilterTypes(item: HotelFilterVm): boolean {
    return this.isCheckboxFilter(item) || this.isStarsFilter(item);
  }

  public isCheckboxFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Checkbox && (item.value as CheckboxFilter[]).length > 0;
  }

  public isRangeFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Range &&
      (item.value as PriceFilter).min !== (item.value as PriceFilter).max;
  }

  public isStarsFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Stars && (item.value as CheckboxFilter[]).length > 0;
  }

  public isExclusiveOffer(item: HotelFilterVm): boolean {
    return item.type === HotelFilterTypes.MemberRatesOnly;
  }

  public getCheckboxLabel(item: CheckboxFilter, type: HotelFilterTypes): string | number {
    return this.withTranslations(type) ? this.labelTranslation(item, type) :
      item.TextTransformed ? item.TextTransformed :
        this.isNoZoneLabel(item) ? this.translateService.translate(NO_ZONE_LABEL) : 
          type === HotelFilterTypes.Score ? this.translateService.translate(getLegendByScore(item.Text)) : item.Text;
  }

  public isNoZoneLabel(item: CheckboxFilter): boolean {
    return hotelFilterIsNoZoneLabel(item);
  }

  public checked(item: CheckboxFilterVm, type: string, isCheckboxFilter: boolean): boolean {
    const validate = this.hotelFilterBarService.checkedHotelCheckbox(
      this.pickedCheckboxes, this.pickedStars, this.selectedStarItems, item, isCheckboxFilter
    );
    if (validate.addStars) {
      this.selectedStarItems.push(item);
    }
    return evalHasFilterByExcludeCollection(type, this.inFilterBar, true, validate.include, item.Text);
  }

  public checkedRanges(type: string): string[] | number[] | RangePicketVm {
    return this.hotelFilterBarService.checkedHotelRanges(this.pickedRanges, type);
  }

  public rangeType(type: string): string {
    return type === HotelFilterTypes.Price ? RangeType.PRICE : null;
  }

  public removeCheckboxFilter(item: CheckboxFilter, type: string, isCheckboxFilter: boolean): void {
    if (isCheckboxFilter) {
      this.remove.emit({
        type: type as HotelFilterTypes,
        value: item.Text,
        removable: true
      });
    } else {

      this.remove.emit({
        type: type as HotelFilterTypes,
        value: this.selectedStarItems.filter(_item => _item.Text !== item.Text)
      });
    }
  }

  public removeRangeFilter(type: string): void {
    this.remove.emit({
      type: type as HotelFilterTypes,
      value: []
    });
  }

  public removeInputFilter(): void {
    this.remove.emit({
      type: HotelFilterTypes.HotelName,
      value: ''
    });
  }

  public removeDiscountFilter(): void {
    this.remove.emit({
      type: HotelFilterTypes.PromotionalRates,
      value: ''
    });
  }

  public removeExclusiveOfferFilter(): void {
    this.remove.emit({
      type: HotelFilterTypes.MemberRatesOnly,
      value: false
    });
  }

  private labelTranslation(item: CheckboxFilter, type: string): string {
    return type === HotelFilterTypes.NonRefundable ?
      this.translateService.translate(HOTEL_NON_REFUNDABLE_TITLES[item.Text]) :
      type === HotelFilterTypes.Category ?
        `${item.Text} ${this.translateService.translate(this.getCategoryLabel(item.Text))}` : item.Text;
  }

  private getCategoryLabel(label: string): string {
    return parseInt(label) === 1 ? STAR_LABEL : STARS_LABEL;
  }

}
